import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '../../../store/PermissionContext';
import { EMULATION_OPERATION_ID } from '../../../constants/reportBugConstants';
import { STORAGE_KEYS } from '../../../constants/storageKeys';
import { endEmulation, readEmulationState } from '../../../utils/emulation';
import EmulationModal from './EmulationModal';

// ── Tailwind class bundles ────────────────────────────────────────────────
// Emulation entry button — outlined neutral chip. Both "Start" and "End"
// variants share the same shell; "End" gets a slightly stronger border.
const EMULATION_BTN_BASE =
  'inline-flex items-center h-8 px-[14px] bg-transparent text-[var(--color-text-primary)]' +
  ' text-[13px] font-hk-medium cursor-pointer whitespace-nowrap' +
  ' border border-solid border-[var(--color-border-strong)]' +
  ' transition-[background-color,border-color,color] duration-[150ms] ease-linear' +
  ' enabled:hover:bg-[var(--color-bg-hover)]' +
  ' disabled:opacity-50 disabled:cursor-not-allowed';

// End-emulation variant — neutral outlined (NOT destructive red), see the
// commentary in the original CSS for why this isn't a danger button.
const EMULATION_BTN_END_EXTRA =
  ' border-[var(--color-text-primary)] text-[var(--color-text-primary)]';

const EMULATION_BTN_LABEL_CLASS = 'leading-none';

// "Emulating as <name>" status chip shown when emulation is active. Truncates
// to 360 px on desktop, 220 px on tablets/phones (<= 1024 px).
const EMULATION_ACTIVE_CLASS =
  'inline-flex items-center gap-3 px-1 text-[13px] text-[var(--color-header-fg)] max-w-[360px]' +
  ' max-[1024px]:max-w-[220px]';

const EMULATION_ACTIVE_LABEL_CLASS =
  'whitespace-nowrap overflow-hidden text-ellipsis font-hk-medium' +
  ' max-[1024px]:text-xs';

const EMULATION_ACTIVE_NAME_CLASS = 'font-hk-semibold ml-1';

const BookMark: React.FC = () => {
  const { t } = useTranslation();
  const { hasOperationPermission } = usePermissions();
  const [emulating, setEmulating] = useState<boolean>(
    () => readEmulationState().active,
  );
  const [emulatedName, setEmulatedName] = useState<string | null>(
    () => readEmulationState().emulatedUserName,
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  // Cross-tab/cross-service sync — if Vue starts/ends emulation in another tab,
  // pick up the change here without a reload.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (
        event.key !== STORAGE_KEYS.isEmulate
        && event.key !== STORAGE_KEYS.emulatedUserName
      ) return;
      const next = readEmulationState();
      setEmulating(next.active);
      setEmulatedName(next.emulatedUserName);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleStart = useCallback((): void => setModalOpen(true), []);
  const handleClose = useCallback((): void => setModalOpen(false), []);
  const handleEnd = useCallback((): void => {
    // `endEmulation` is async (clears the emulated IDB slot) — await
    // before reloading so the next boot doesn't read the just-cleared
    // store mid-deletion. The reload mirrors Vue's `pageReload()`
    // pattern in UserEmulation.vue:183-186.
    void endEmulation().finally(() => {
      window.location.reload();
    });
  }, []);

  /*
    Permission gate — fail CLOSED, matching Vue's `UserEmulation.vue`
    (`v-if="isEmulateOperationPermission"`, where the flag is
    `operationPermissions[OPERATION_ID].hasPermission || false`). The button
    is hidden unless the user explicitly has the emulation operation
    permission. `hasOperationPermission` returns false until permissions land,
    so the button also stays hidden during the skeleton phase. While already
    emulating we always show the control so "End emulation" stays reachable
    (Vue force-sets the flag true when `isEmulate === 'true'`).
  */
  const canEmulate = hasOperationPermission(EMULATION_OPERATION_ID);
  if (!emulating && !canEmulate) return null;

  if (emulating) {
    const displayName = emulatedName ?? t('emulation.unknownUser');
    return (
      <div className={EMULATION_ACTIVE_CLASS} aria-live="polite">
        <span className={EMULATION_ACTIVE_LABEL_CLASS}>
          {t('emulation.emulatingAs')}
          <span className={EMULATION_ACTIVE_NAME_CLASS}>{displayName}</span>
        </span>
        <button
          type="button"
          className={EMULATION_BTN_BASE + EMULATION_BTN_END_EXTRA}
          onClick={handleEnd}
          aria-label={t('emulation.end')}
          title={t('emulation.end')}
        >
          <span className={EMULATION_BTN_LABEL_CLASS}>{t('emulation.end')}</span>
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className={EMULATION_BTN_BASE}
        onClick={handleStart}
        aria-label={t('header.emulation')}
        title={t('header.emulation')}
      >
        <span className={EMULATION_BTN_LABEL_CLASS}>{t('header.emulation')}</span>
      </button>
      <EmulationModal open={modalOpen} onClose={handleClose} />
    </>
  );
};

export default BookMark;
